import FeatureFlagsContext from 'context/FeatureFlagsContext';
import { useContext, useEffect, useRef, useState } from 'react';
import {
  lastFeedbackTriggeredDate,
  successfulActionCount,
} from 'states/persistInStorage';
import {
  FeedbackTriggerConfig,
  parseFeatureFlagObject,
} from 'utils/featureFlagUtils';
import {
  getItemFromStorage,
  updateStateAndStorage,
} from 'utils/MMKVStorageUtils';

const useFeedbackCount = (sessionId: string) => {
  const {
    flags: { feedback_trigger },
  } = useContext(FeatureFlagsContext);

  const [feedbackSuccessCount, setFeedbackSuccessCount] = useState(
    Number(getItemFromStorage('successfulActionCount')) || 0
  );

  const lastSessionIdRef = useRef<string | null>(null);
  const feedbackTriggerConfig = parseFeatureFlagObject<FeedbackTriggerConfig>(
    feedback_trigger,
    'feedback_trigger'
  );
  const { feedbackTriggerType, values, frequency } = feedbackTriggerConfig;

  /** Updates the successful action count in storage (for byCount) */
  const updateCountStorage = (newCount: number) => {
    updateStateAndStorage(
      successfulActionCount,
      'successfulActionCount',
      newCount
    );
    setFeedbackSuccessCount(newCount);
  };

  /** Updates the last feedback triggered date in storage (for byFrequency) */
  const updateFrequencyStorage = () => {
    updateStateAndStorage(
      lastFeedbackTriggeredDate,
      'lastFeedbackTriggeredDate',
      new Date().toISOString()
    );
  };

  /** Check if feedback should be shown based on count */
  const shouldShowFeedbackByCount = (count: number) => {
    return values?.includes(count) ?? false;
  };

  /** Check if feedback should be shown based on frequency */
  const shouldShowFeedbackByFrequency = () => {
    const lastTriggeredDate = getItemFromStorage('lastFeedbackTriggeredDate');
    if (!lastTriggeredDate) {
      updateFrequencyStorage();
      return false;
    }

    const currentDate = new Date();
    const lastFeedbackDate = new Date(lastTriggeredDate);
    const daysDifference =
      (currentDate.getTime() - lastFeedbackDate.getTime()) / (1000 * 3600 * 24);
    return frequency !== undefined && daysDifference >= frequency;
  };

  /** Determine if feedback should be shown based on type */
  const shouldShowFeedback = () => {
    if (feedbackTriggerType === 'byCount') {
      return shouldShowFeedbackByCount(feedbackSuccessCount);
    }
    if (feedbackTriggerType === 'byFrequency') {
      return shouldShowFeedbackByFrequency();
    }
    return false;
  };

  /** Handle updates for 'byCount' feedback type */
  useEffect(() => {
    if (feedbackTriggerType !== 'byCount' || !sessionId) return;
    if (Number.isNaN(feedbackSuccessCount))
      return console.error('Invalid successfulActionCount.');
    if (sessionId === lastSessionIdRef.current) return;

    updateCountStorage(feedbackSuccessCount + 1);
    lastSessionIdRef.current = sessionId;
  }, [sessionId, feedbackTriggerType]);

  /** Handle updates for 'byFrequency' feedback type */
  useEffect(() => {
    if (feedbackTriggerType !== 'byFrequency' || !sessionId) return;
    if (sessionId === lastSessionIdRef.current) return;

    if (shouldShowFeedbackByFrequency()) {
      setTimeout(updateFrequencyStorage, 1000);
    }

    lastSessionIdRef.current = sessionId;
  }, [sessionId, feedbackTriggerType]);

  return {
    shouldShowFeedback: shouldShowFeedback(),
    feedbackSuccessCount,
  };
};

export default useFeedbackCount;
